/* ============
 * Marque (SP-13660)
 * ============
 *
 * Deux marques cohabitent dans l'Espace client : « Student Pop » par défaut et
 * « Talent Work » pour les sociétés qui travaillent en mode intérim
 * (`company.isTempAgencyMode`). En mode intérim le lexique bascule également :
 * Student(s) → intérimaire(s), étudiant / prestataire / indépendant → intérimaire,
 * prestation → mission.
 *
 * Deux leviers :
 *
 * 1. `$brand` — pour les libellés écrits en dur dans les templates
 *    (`$brand.label`, `$brand.workers`, `$brand.isTempAgency`…).
 * 2. L'overlay i18n — pour les ~110 chaînes de `fr.json`. Chaque clé qui change
 *    porte une variante `<clé>.temp_agency` ; l'overlay recopie ces variantes
 *    par-dessus les clés de base dès que la société est en mode intérim, et
 *    restaure les valeurs d'origine sinon. Les call-sites ne changent pas :
 *    `$t` et les clés construites à l'exécution en profitent aussi.
 */

import { effectScope, watch } from 'vue';
import { useAccountStore } from '@/stores/account';

const VARIANT_SUFFIX = '.temp_agency';
const VARIANT_LOCALE = 'fr';

// SP-13795 / SP-13832 — le drapeau intérim se lit via le getter du module account
// (société enfant en snake_case, `state.company` encore `null` avant `GET /me`),
// jamais directement dans `state.account.company`.
const isTempAgency = () => Boolean(useAccountStore().isTempAgencyMode);

const TERMS = {
  default: {
    label: 'Student Pop',
    worker: 'Student',
    workers: 'Students',
    workerCap: 'Student',
    workersCap: 'Students',
    workerLower: 'student',
    workersLower: 'students',
    assignment: 'prestation',
    assignments: 'prestations',
    assignmentCap: 'Prestation',
    logo: 'logo-sp.svg',
    cgu: 'https://www.studentpop.fr/conditions-generales',
  },
  tempAgency: {
    label: 'Talent Work',
    worker: 'intérimaire',
    workers: 'intérimaires',
    workerCap: 'Intérimaire',
    workersCap: 'Intérimaires',
    workerLower: 'intérimaire',
    workersLower: 'intérimaires',
    assignment: 'mission',
    assignments: 'missions',
    assignmentCap: 'Mission',
    logo: 'logo-tw.svg',
    cgu: 'https://www.studentpop.fr/conditions-generales-interim',
  },
};

/**
 * Construit, à partir des clés plates de `fr.json`, les deux jeux de messages
 * à appliquer : les variantes intérim et les valeurs d'origine correspondantes.
 */
function buildOverlays(messages) {
  const tempAgency = {};
  const base = {};
  Object.keys(messages).forEach((key) => {
    if (key.slice(-VARIANT_SUFFIX.length) !== VARIANT_SUFFIX) return;
    const baseKey = key.slice(0, -VARIANT_SUFFIX.length);
    if (typeof messages[baseKey] !== 'string') return;
    tempAgency[baseKey] = messages[key];
    base[baseKey] = messages[baseKey];
  });
  return { tempAgency, base };
}

export default {
  install(app, { i18n }) {
    Object.defineProperty(app.config.globalProperties, '$brand', {
      get() {
        const tempAgency = isTempAgency();
        const terms = tempAgency ? TERMS.tempAgency : TERMS.default;
        return {
          isTempAgency: tempAgency,
          label: terms.label,
          worker: terms.worker,
          workers: terms.workers,
          workerCap: terms.workerCap,
          workersCap: terms.workersCap,
          workerLower: terms.workerLower,
          workersLower: terms.workersLower,
          assignment: terms.assignment,
          assignments: terms.assignments,
          assignmentCap: terms.assignmentCap,
          logo: terms.logo,
          url: {
            cgu: terms.cgu,
          },
        };
      },
    });

    if (!i18n) return;

    const overlays = buildOverlays(i18n.getLocaleMessage(VARIANT_LOCALE));

    const applyOverlay = (tempAgency) => {
      i18n.mergeLocaleMessage(VARIANT_LOCALE, tempAgency ? overlays.tempAgency : overlays.base);
    };

    // La société n'est connue qu'après le chargement du compte, et elle change
    // au vol lors d'un switch de compte : on suit l'état plutôt que de le lire
    // une seule fois au boot.
    applyOverlay(isTempAgency());
    // `install()` runs outside any component: without an explicit scope the
    // watcher would have no owner and could never be stopped. The scope lives
    // as long as the application, which is exactly the intended lifetime.
    effectScope(true).run(() => {
      watch(isTempAgency, applyOverlay);
    });
  },
};
